import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Button,
  Badge,
  Skeleton,
} from "@asm/ui";
import {
  Scan,
  Globe,
  ArrowRight,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  AlertTriangle,
  ShieldAlert,
  ShieldCheck,
  Shield,
  ChevronRight,
  Calendar,
  ExternalLink,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { scansApi, domainsApi, schedulesApi, type Scan as ScanType, type Domain } from "@/lib/api";
import { simplifyResults, type PriorityAction, type BusinessCategory } from "@/lib/simplify-results";
import type { RiskLevel } from "@/lib/api";

// Risk level visual config
const RISK_STYLES: Record<RiskLevel, { label: string; color: string; bg: string; border: string }> = {
  critical: { label: "Critical", color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/30" },
  high: { label: "High", color: "text-orange-400", bg: "bg-orange-500/10", border: "border-orange-500/30" },
  medium: { label: "Medium", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  low: { label: "Low", color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/30" },
  info: { label: "Excellent", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
};

const RISK_ICONS: Record<RiskLevel, typeof Shield> = {
  critical: ShieldAlert,
  high: AlertTriangle,
  medium: Shield,
  low: ShieldCheck,
  info: ShieldCheck,
};

const CATEGORY_LABELS: Record<BusinessCategory, string> = {
  email: "Email",
  website: "Website",
  domain: "Domain",
  exposure: "Exposure",
  brand: "Brand",
};

// Status icon component
function StatusIcon({ status }: { status: ScanType["status"] }) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
    case "failed":
      return <XCircle className="h-4 w-4 text-red-400" />;
    case "running":
      return <Loader2 className="h-4 w-4 text-cyan-400 animate-spin" />;
    case "pending":
      return <Clock className="h-4 w-4 text-amber-400" />;
    case "cancelled":
      return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    default:
      return null;
  }
}

// Compact action item for dashboard
function CompactAction({ action }: { action: PriorityAction }) {
  const style = RISK_STYLES[action.severity];

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${style.border} ${style.bg}`}>
      <div className={`mt-0.5 p-1 rounded ${style.bg} border ${style.border} shrink-0`}>
        <span className={`text-xs font-bold font-mono ${style.color}`}>{action.priority}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium leading-tight">{action.title}</h4>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{action.description}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
            {CATEGORY_LABELS[action.category]}
          </span>
          {action.learnMoreUrl && (
            <a
              href={action.learnMoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1 text-[10px] ${style.color} hover:underline`}
            >
              Fix guide <ExternalLink className="h-2.5 w-2.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t } = useTranslation("dashboard");
  const { t: tCommon } = useTranslation("common");

  // Fetch recent scans
  const { data: scansData, isLoading: scansLoading } = useQuery({
    queryKey: ["scans", { per_page: 5 }],
    queryFn: () => scansApi.list({ per_page: 5 }),
  });

  // Fetch domains
  const { data: domains, isLoading: domainsLoading } = useQuery({
    queryKey: ["domains"],
    queryFn: () => domainsApi.list(),
  });

  // Fetch latest completed scan for security posture
  const latestCompletedScan = scansData?.scans?.find(s => s.status === "completed");

  const { data: latestResults } = useQuery({
    queryKey: ["scanResults", latestCompletedScan?.id],
    queryFn: () => scansApi.getResults(latestCompletedScan!.id),
    enabled: !!latestCompletedScan?.id,
  });

  // Fetch schedules for next scan info
  const { data: schedulesData } = useQuery({
    queryKey: ["schedules", { per_page: 50 }],
    queryFn: () => schedulesApi.list({ per_page: 50 }),
    staleTime: 60_000,
  });

  // Derive security posture from latest scan
  const posture = latestResults ? simplifyResults(latestResults) : null;

  const scans = scansData?.scans || [];
  const totalDomains = domains?.length || 0;
  const verifiedDomains = domains?.filter((d) => d.verified_at).length || 0;
  const unverifiedDomains = domains?.filter((d) => !d.verified_at) || [];

  // Find next scheduled scan
  const nextSchedule = schedulesData?.schedules
    ?.filter(s => s.enabled && s.next_run_at)
    ?.sort((a, b) => new Date(a.next_run_at).getTime() - new Date(b.next_run_at).getTime())
    ?.[0];

  const statusStyles: Record<ScanType["status"], string> = {
    pending: "border-amber-500/30 text-amber-400 bg-amber-500/10",
    running: "border-cyan-500/30 text-cyan-400 bg-cyan-500/10",
    completed: "border-emerald-500/30 text-emerald-400 bg-emerald-500/10",
    failed: "border-red-500/30 text-red-400 bg-red-500/10",
    cancelled: "border-muted text-muted-foreground bg-muted/50",
  };

  return (
    <div className="space-y-6">
      {/* Header: Security Posture Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("title")}</h1>
          <p className="text-muted-foreground text-sm">{t("description")}</p>
        </div>
        <Button asChild className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
          <Link to="/scans/new">
            <Plus className="mr-2 h-4 w-4" />
            {t("newScan")}
          </Link>
        </Button>
      </div>

      {/* Value-centric Stats */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
        {/* Risk Level */}
        <div className={`rounded-xl border p-5 ${posture ? `${RISK_STYLES[posture.score.riskLevel].border} ${RISK_STYLES[posture.score.riskLevel].bg}` : "border-border bg-card/50"}`}>
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">
            Security Posture
          </span>
          {scansLoading ? (
            <Skeleton className="h-8 w-24 mt-2" />
          ) : posture ? (
            <>
              <div className={`flex items-center gap-2 mt-2`}>
                {(() => {
                  const Icon = RISK_ICONS[posture.score.riskLevel];
                  return <Icon className={`h-5 w-5 ${RISK_STYLES[posture.score.riskLevel].color}`} />;
                })()}
                <span className={`text-2xl font-bold ${RISK_STYLES[posture.score.riskLevel].color}`}>
                  {RISK_STYLES[posture.score.riskLevel].label}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">{posture.score.assessment}</p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground mt-2">—</div>
              <p className="text-xs text-muted-foreground mt-1">Run your first scan</p>
            </>
          )}
        </div>

        {/* Urgent Issues */}
        <div className={`rounded-xl border p-5 ${posture && posture.urgentCount > 0 ? "border-red-500/30 bg-red-500/5" : "border-border bg-card/50"}`}>
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">
            Urgent Issues
          </span>
          {scansLoading ? (
            <Skeleton className="h-8 w-16 mt-2" />
          ) : (
            <>
              <div className={`text-3xl font-bold font-mono mt-2 ${
                posture
                  ? posture.urgentCount > 0 ? "text-red-400" : "text-emerald-400"
                  : "text-muted-foreground"
              }`}>
                {posture ? posture.urgentCount : "—"}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {posture
                  ? posture.urgentCount > 0
                    ? "Require immediate attention"
                    : "No critical or high issues"
                  : "No data yet"
                }
              </p>
            </>
          )}
        </div>

        {/* Domains Monitored */}
        <div className="rounded-xl border border-border bg-card/50 p-5">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">
            Domains Monitored
          </span>
          {domainsLoading ? (
            <Skeleton className="h-8 w-20 mt-2" />
          ) : (
            <>
              <div className="flex items-baseline gap-1.5 mt-2">
                <span className="text-3xl font-bold font-mono text-foreground">{verifiedDomains}</span>
                {totalDomains > 0 && (
                  <span className="text-sm text-muted-foreground font-mono">/ {totalDomains}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalDomains === 0
                  ? "Add your first domain"
                  : unverifiedDomains.length > 0
                    ? `${unverifiedDomains.length} pending verification`
                    : "All domains verified"
                }
              </p>
            </>
          )}
        </div>

        {/* Next Scheduled Scan */}
        <div className="rounded-xl border border-border bg-card/50 p-5">
          <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">
            Next Scan
          </span>
          {nextSchedule ? (
            <>
              <div className="flex items-center gap-2 mt-2">
                <Calendar className="h-4 w-4 text-cyan-400" />
                <span className="text-lg font-semibold">
                  {new Date(nextSchedule.next_run_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                  })}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {new Date(nextSchedule.next_run_at).toLocaleTimeString(undefined, {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {" — "}{nextSchedule.name}
              </p>
            </>
          ) : (
            <>
              <div className="text-2xl font-bold text-muted-foreground mt-2">—</div>
              <p className="text-xs text-muted-foreground mt-1">
                <Link to="/schedules" className="text-cyan-400 hover:underline">Set up a schedule</Link>
              </p>
            </>
          )}
        </div>
      </div>

      {/* Attention Required Section */}
      {posture && posture.priorityActions.length > 0 && (
        <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 rounded-full bg-red-500" />
              <div>
                <h2 className="text-lg font-semibold">Attention Required</h2>
                <p className="text-xs text-muted-foreground">
                  Top issues from your latest scan of{" "}
                  <span className="font-mono">{latestCompletedScan?.target}</span>
                </p>
              </div>
            </div>
            {latestCompletedScan && (
              <Button variant="ghost" size="sm" asChild className="gap-1 text-cyan-500 hover:text-cyan-400">
                <Link to={`/scans/${latestCompletedScan.id}`}>
                  View full report
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </Button>
            )}
          </div>
          <div className="p-5 space-y-2">
            {posture.priorityActions.slice(0, 3).map((action) => (
              <CompactAction key={action.id} action={action} />
            ))}
            {posture.priorityActions.length > 3 && (
              <Link
                to={`/scans/${latestCompletedScan?.id}`}
                className="block text-center text-sm text-cyan-400 hover:text-cyan-300 py-2"
              >
                +{posture.priorityActions.length - 3} more items
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Unverified domains prompt */}
      {unverifiedDomains.length > 0 && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-400" />
              <div>
                <h3 className="font-medium text-sm">
                  {unverifiedDomains.length} domain{unverifiedDomains.length > 1 ? "s" : ""} pending verification
                </h3>
                <p className="text-xs text-muted-foreground">
                  Verify domains to unlock active scanning modules like port scanning and subdomain bruteforce.
                </p>
              </div>
            </div>
            <Button asChild variant="outline" size="sm" className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10 shrink-0">
              <Link to="/domains">
                Verify now
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* Content Grid */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Recent Scans */}
        <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 rounded-full bg-cyan-500" />
              <h2 className="text-lg font-semibold">{t("recentScans.title")}</h2>
            </div>
            <Button variant="ghost" size="sm" asChild className="gap-1 text-cyan-500 hover:text-cyan-400">
              <Link to="/scans">
                {tCommon("buttons.viewAll")}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </div>

          <div className="p-5">
            {scansLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : scans?.length ? (
              <div className="space-y-2">
                {scans.slice(0, 5).map((scan) => (
                  <Link
                    key={scan.id}
                    to={`/scans/${scan.id}`}
                    className="group flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20 hover:bg-muted/40 hover:border-cyan-500/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg border ${statusStyles[scan.status]}`}>
                        <StatusIcon status={scan.status} />
                      </div>
                      <div>
                        <p className="font-mono text-sm font-medium">{scan.target}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(scan.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={statusStyles[scan.status]}>
                        {tCommon(`status.${scan.status}`)}
                      </Badge>
                      <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-cyan-400 transition-colors" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <Scan className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-sm">{t("recentScans.empty")}</p>
                <Button asChild className="mt-4 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold">
                  <Link to="/scans/new">
                    <Plus className="mr-2 h-4 w-4" />
                    Start your first scan
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Domains */}
        <div className="rounded-xl border border-border bg-card/50 overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="h-6 w-1 rounded-full bg-emerald-500" />
              <h2 className="text-lg font-semibold">{t("domains.title")}</h2>
            </div>
            <Button variant="ghost" size="sm" asChild className="gap-1 text-emerald-500 hover:text-emerald-400">
              <Link to="/domains">
                {tCommon("buttons.viewAll")}
                <ArrowRight className="h-3 w-3" />
              </Link>
            </Button>
          </div>

          <div className="p-5">
            {domainsLoading ? (
              <div className="space-y-3">
                {[...Array(3)].map((_, i) => (
                  <Skeleton key={i} className="h-14 w-full rounded-lg" />
                ))}
              </div>
            ) : domains?.length ? (
              <div className="space-y-2">
                {domains.slice(0, 5).map((domain: Domain) => (
                  <div
                    key={domain.id}
                    className="flex items-center justify-between p-3 rounded-lg border border-border bg-muted/20"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-muted/50 border border-border">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-mono text-sm font-medium">{domain.domain}</p>
                        <p className="text-xs text-muted-foreground">
                          {tCommon("date.added", { date: new Date(domain.created_at).toLocaleDateString() })}
                        </p>
                      </div>
                    </div>
                    {domain.verified_at ? (
                      <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/20">
                        <CheckCircle2 className="h-3 w-3 mr-1" />
                        {tCommon("status.verified")}
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-amber-400 border-amber-500/30 bg-amber-500/10">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        {tCommon("status.unverified")}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <Globe className="h-10 w-10 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground text-sm">{t("domains.empty")}</p>
                <Button asChild variant="outline" className="mt-4 border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/10">
                  <Link to="/domains">
                    <Plus className="mr-2 h-4 w-4" />
                    Add your first domain
                  </Link>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
